// Portfolio Dashboard export: turns the backend's sheet-shaped JSON into the reviewed
// workbook. The backend sends column headers verbatim, so nothing here renames a field --
// if a column looks wrong, the Data Dictionary and the backend payload are the source of
// truth, not this file.
//
// The point of the export is that Excel and Sheets can use it without cleaning: real dates
// and numbers rather than pre-formatted strings, one header row, no merged cells, an
// autofilter and frozen header on every sheet.

import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

export type PortfolioExportPayload = Record<string, Record<string, unknown>[]>

// Sheet order in the workbook: data first, context last.
const SHEET_ORDER = [
  "Run Info",
  "Deal Master",
  "Performance Tape",
  "Deal Monthly Tape",
  "Pool Master",
  "Pool Monthly Collections Tape",
  "Vintage Summary",
  "Vintage Recovery %"
]

// Every money column is in the reporting currency, USD (see Run Info), so it carries the sign.
const MONEY_FORMAT = '"$"#,##0.00'
// Two decimals, as the reviewed workbook has them: a variance of -14.69% reads as -14.7% at
// one decimal, and the tape is read at that precision.
const PERCENT_FORMAT = "0.00%"
const MULTIPLIER_FORMAT = '0.00"x"'
const DATE_FORMAT = "yyyy-mm-dd"
const PERIOD_FORMAT = "yyyy-mm"
const INTEGER_FORMAT = "0"

// Styled after the investor Performance Tape the team shares, so the export reads the same:
// blue wrapped headers, banded rows, statuses coloured by outcome.
const solid = (argb: string): ExcelJS.Fill => ({ type: "pattern", pattern: "solid", fgColor: { argb } })
const FONT_NAME = "Calibri"
const TITLE_FILL = solid("FF1F4E78")
const TITLE_FONT: Partial<ExcelJS.Font> = { name: FONT_NAME, bold: true, size: 14, color: { argb: "FFFFFFFF" } }
const HEADER_FILL = solid("FF386CA3")
const HEADER_FONT: Partial<ExcelJS.Font> = { name: FONT_NAME, bold: true, size: 11, color: { argb: "FFFFFFFF" } }
const BODY_FONT: Partial<ExcelJS.Font> = { name: FONT_NAME, size: 11 }
const BAND_FILL = solid("FFE6F2FF")
const HEADER_HEIGHT = 41.25
const TITLE_HEIGHT = 48.75

const STATUS_FILLS: Record<string, ExcelJS.Fill> = {
  "On Track": solid("FFB7E1CD"),
  Ahead: solid("FFC9DAF8"),
  "Timing Review": solid("FFF4CCCC"),
  New: solid("FFEFEFEF")
}
const STATUS_COLUMN = "Performance Status"

// Leading columns that identify a row stay in view while the sheet scrolls sideways.
const KEY_COLUMNS = ["Reporting Period", "Contract ID", "User ID", "Recoupment Pool ID", "Deal ID", "Section", "Item"]

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// Vintage Summary's cohort-age columns: M0, M1, ... Money, like every other cash column,
// but the header carries no word for the rules below to match on. Vintage Recovery %'s
// mirror of them is named "M0 %", so it falls to the percent rule instead and never
// reaches this one.
const COHORT_AGE = /^M\d+$/

// Columns whose name alone determines the format. The first match wins, so these run
// most specific first: "MOIC to Date" is a multiplier, not a date, and "Months Since
// Advance Deployment" is a count, not money.
const FORMAT_RULES: { matches: (column: string) => boolean; format: string }[] = [
  { matches: (c) => c.startsWith("MOIC") || c.endsWith("Speed Index"), format: MULTIPLIER_FORMAT },
  { matches: (c) => c.endsWith("Percentage") || c.endsWith("%"), format: PERCENT_FORMAT },
  // Actual days: when the contract was signed and when an advance was paid out.
  { matches: (c) => c === "Signed Date" || c.endsWith("Advance Deployment Date"), format: DATE_FORMAT },
  // Every other date denotes a month -- sent as that month's last day -- so it shows as one.
  {
    matches: (c) => c === "Reporting Period" || c.endsWith("Deployment Month") || c.endsWith("Date"),
    format: PERIOD_FORMAT
  },
  // "Recordings" covers Pool Master's scope counts (Recouping Recordings, Excluded
  // Recordings); "In Scope" covers Recordings In Scope. All are counts, not money.
  {
    matches: (c) =>
      c.includes("Months") ||
      c.endsWith("Count") ||
      c.endsWith("Number") ||
      c.endsWith("Priority") ||
      c.endsWith("In Scope") ||
      c.endsWith("Recordings"),
    format: INTEGER_FORMAT
  },
  {
    matches: (c) =>
      c.includes("Amount") ||
      c.includes("Balance") ||
      c.includes("Collections") ||
      c.includes("Cash") ||
      c.includes("Revenue") ||
      c.includes("Profit") ||
      c.includes("Applied") ||
      c.includes("Payable") ||
      c.includes("Recoupment") ||
      c.includes("Advance") ||
      COHORT_AGE.test(c),
    format: MONEY_FORMAT
  }
]

// Identifiers stay on General, ahead of every rule: "Recoupment Pool ID" would otherwise hit
// the money rule on "Recoupment" and print as 1234.00, and Artist Spotify ID is text.
const isIdentifier = (column: string) => column.endsWith(" ID")

const formatFor = (column: string): string | undefined =>
  isIdentifier(column) ? undefined : FORMAT_RULES.find((r) => r.matches(column))?.format

// Run Info's Value column carries whole paragraphs explaining the model. Widened rather
// than wrapped they spill across the neighbouring cells and read as truncated, which is
// how those explanations went unread. Past this length a column is wrapped at a fixed
// width instead; row heights are left unset so Excel and Sheets auto-fit the paragraph.
const LONG_TEXT_LENGTH = 90
const LONG_TEXT_WIDTH = 80

const longestIn = (values: unknown[]): number =>
  values.reduce<number>((longest, value) => {
    const length = value === null || value === undefined ? 0 : String(value).length
    return Math.max(longest, length)
  }, 0)

// Headers wrap, so a column only has to fit its longest header word and its values; money
// gets room for the currency sign and thousands separators the format adds.
const widthFor = (column: string, longestValue: number, format: string | undefined): number => {
  const longestWord = Math.max(...column.split(" ").map((word) => word.length))
  const valueWidth = longestValue + (format === MONEY_FORMAT ? 5 : 2)
  return Math.min(Math.max(longestWord + 2, valueWidth, 12), 60)
}

const isDateColumn = (column: string) => {
  const format = formatFor(column)
  return format === DATE_FORMAT || format === PERIOD_FORMAT
}

// Excel wants a Date object to apply a date format; the backend sends ISO strings. Only
// date columns are converted: a stray ISO string elsewhere -- Run Info's report date sits
// in a column called "Value" alongside text and counts -- would otherwise become a date
// cell with no date format, which Excel renders as a serial number.
const coerce = (column: string, value: unknown): unknown => {
  if (value === null || value === undefined || value === "") return null
  if (typeof value === "string" && ISO_DATE.test(value) && isDateColumn(column)) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(Date.UTC(year, month - 1, day))
  }
  return value
}

const addSheet = (workbook: ExcelJS.Workbook, name: string, rows: Record<string, unknown>[], title?: string) => {
  const sheet = workbook.addWorksheet(name)
  if (!rows.length) {
    sheet.addRow(["No rows"])
    return
  }

  // Union of keys rather than the first row's, so a null-only column is never dropped.
  const columns: string[] = []
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columns.includes(key)) columns.push(key)
    })
  })

  if (title) {
    const titleRow = sheet.addRow([title])
    sheet.mergeCells(1, 1, 1, columns.length)
    titleRow.height = TITLE_HEIGHT
    titleRow.getCell(1).fill = TITLE_FILL
    titleRow.getCell(1).font = TITLE_FONT
    titleRow.getCell(1).alignment = { horizontal: "left", vertical: "middle", indent: 1 }
  }
  const headerRowNumber = title ? 2 : 1

  const headerRow = sheet.addRow(columns)
  headerRow.height = HEADER_HEIGHT
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL
    cell.font = HEADER_FONT
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true }
  })

  rows.forEach((row) => sheet.addRow(columns.map((column) => coerce(column, row[column]))))

  columns.forEach((column, index) => {
    const worksheetColumn = sheet.getColumn(index + 1)
    const longest = longestIn(rows.map((row) => row[column]))
    const wrapped = longest > LONG_TEXT_LENGTH
    const format = formatFor(column)

    worksheetColumn.width = wrapped ? LONG_TEXT_WIDTH : widthFor(column, longest, format)

    for (let rowNumber = headerRowNumber + 1; rowNumber <= headerRowNumber + rows.length; rowNumber++) {
      const cell = sheet.getRow(rowNumber).getCell(index + 1)
      cell.font = BODY_FONT
      // Banded on every cell, empty ones included, so a blank never breaks the stripe.
      const banded = (rowNumber - headerRowNumber) % 2 === 0
      const statusFill = column === STATUS_COLUMN ? STATUS_FILLS[String(cell.value)] : undefined
      if (statusFill) cell.fill = statusFill
      else if (banded) cell.fill = BAND_FILL
      if (format && cell.value !== null) cell.numFmt = format
      cell.alignment = wrapped ? { wrapText: true, vertical: "top" } : { vertical: "middle" }
    }
  })

  const keyCount = columns.findIndex((column) => !KEY_COLUMNS.includes(column))
  sheet.views = [{ state: "frozen", ySplit: headerRowNumber, xSplit: keyCount > 0 ? keyCount : 0 }]
  sheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columns.length }
  }
}

export const buildWorkbook = (payload: PortfolioExportPayload): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = "Kahuna"

  // Follow the agreed order, then append anything the backend adds later so a new sheet
  // still reaches the file instead of being silently dropped.
  const names = [
    ...SHEET_ORDER.filter((n) => n in payload),
    ...Object.keys(payload).filter((n) => !SHEET_ORDER.includes(n))
  ]
  // Run Info opens the file, so it carries the title band the investor workbook's cover has.
  const title = `Kahuna Portfolio Export | Report Date ${reportDateOf(payload).slice(0, 7)}`
  names.forEach((name) => addSheet(workbook, name, payload[name] ?? [], name === "Run Info" ? title : undefined))

  return workbook
}

export const buildPortfolioWorkbook = async (payload: PortfolioExportPayload): Promise<Blob> => {
  const workbook = buildWorkbook(payload)
  workbook.created = new Date()

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
}

export const reportDateOf = (payload: PortfolioExportPayload): string => {
  const runInfo = payload["Run Info"] ?? []
  const row = runInfo.find((entry) => entry.Item === "Report Date")
  return typeof row?.Value === "string" ? row.Value : new Date().toISOString().slice(0, 10)
}

export const downloadPortfolioExport = async (payload: PortfolioExportPayload) => {
  const blob = await buildPortfolioWorkbook(payload)
  saveAs(blob, `kahuna_portfolio_export_${reportDateOf(payload)}.xlsx`)
}
